/**
 * @license
 * Copyright 2026 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 *
 * g3-prettier-ignore-file
 */

/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import { webhooksCreate } from "../funcs/webhooks-create.js";
import { webhooksDelete } from "../funcs/webhooks-delete.js";
import { webhooksGet } from "../funcs/webhooks-get.js";
import { webhooksList } from "../funcs/webhooks-list.js";
import { webhooksPing } from "../funcs/webhooks-ping.js";
import { webhooksRotateSigningSecret } from "../funcs/webhooks-rotate-signing-secret.js";
import { webhooksUpdate } from "../funcs/webhooks-update.js";
import { ClientSDK, RequestOptions } from "../lib/sdks.js";
import * as interactions from "../models/interactions/index.js";
import {
  CreateWebhookParams,
  DeleteWebhookParams,
  GetWebhookParams,
  ListWebhooksParams,
  UpdateWebhookParams,
} from "../models/operations/method-params.js";
import * as webhooks from "../models/webhooks/index.js";
import { APIPromise, unwrapAsAPIPromise } from "../types/async.js";

export class Webhooks extends ClientSDK {
  /**
   * Creates a new Webhook.
   */
  create(
    params: CreateWebhookParams,
    options?: RequestOptions,
  ): APIPromise<webhooks.Webhook> {
    const { api_version, ...body } = params;
    return unwrapAsAPIPromise(webhooksCreate(
      this,
      body,
      api_version,
      options,
    ));
  }

  /**
   * Lists all Webhooks.
   */
  list(
    params?: ListWebhooksParams,
    options?: Omit<RequestOptions, "extra_body">,
  ): APIPromise<webhooks.WebhookListResponse> {
    return unwrapAsAPIPromise(webhooksList(
      this,
      params?.api_version,
      params?.page_size,
      params?.page_token,
      options,
    ));
  }

  /**
   * Gets a specific Webhook.
   */
  get(
    id: string,
    params?: GetWebhookParams,
    options?: Omit<RequestOptions, "extra_body">,
  ): APIPromise<webhooks.Webhook> {
    return unwrapAsAPIPromise(webhooksGet(
      this,
      id,
      params?.api_version,
      options,
    ));
  }

  /**
   * Updates an existing Webhook.
   */
  update(
    id: string,
    params?: UpdateWebhookParams,
    options?: RequestOptions,
  ): APIPromise<webhooks.Webhook> {
    const { api_version, update_mask, ...body$body } = params ?? {};
    const body = params === undefined || Object.keys(body$body).length === 0
      ? undefined
      : body$body;
    return unwrapAsAPIPromise(webhooksUpdate(
      this,
      id,
      api_version,
      update_mask,
      body,
      options,
    ));
  }

  /**
   * Deletes a Webhook.
   */
  delete(
    id: string,
    params?: DeleteWebhookParams,
    options?: Omit<RequestOptions, "extra_body">,
  ): APIPromise<interactions.Empty> {
    return unwrapAsAPIPromise(webhooksDelete(
      this,
      id,
      params?.api_version,
      options,
    ));
  }

  /**
   * Generates a new signing secret for a Webhook.
   */
  rotateSigningSecret(
    id: string,
    api_version?: string | undefined,
    body?: webhooks.RotateSigningSecretRequest | undefined,
    options?: RequestOptions,
  ): APIPromise<webhooks.WebhookRotateSigningSecretResponse> {
    return unwrapAsAPIPromise(webhooksRotateSigningSecret(
      this,
      id,
      api_version,
      body,
      options,
    ));
  }

  /**
   * Sends a ping event to a Webhook.
   */
  ping(
    id: string,
    api_version?: string | undefined,
    body?: webhooks.PingWebhookRequest | undefined,
    options?: RequestOptions,
  ): APIPromise<webhooks.WebhookPingResponse> {
    return unwrapAsAPIPromise(webhooksPing(
      this,
      id,
      api_version,
      body,
      options,
    ));
  }
}
